import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useMailStore } from './mailStore';

console.log('[composeTabsStore] Module loading...');

const safeStateStorage = {
  getItem: (name: string) => {
    try {
      return localStorage.getItem(name);
    } catch (error) {
      console.warn('[composeTabsStore] localStorage getItem failed:', error);
      return null;
    }
  },
  setItem: (name: string, value: string) => {
    try {
      localStorage.setItem(name, value);
    } catch (error: unknown) {
      const err = error as DOMException;
      const isQuotaExceeded = err?.name === 'QuotaExceededError' || err?.name === 'NS_ERROR_DOM_QUOTA_REACHED' || err?.code === 22 || err?.code === 1014;
      if (isQuotaExceeded) {
        console.warn('[composeTabsStore] localStorage quota exceeded, clearing persisted compose tabs');
        try {
          localStorage.removeItem(name);
        } catch (cleanupError) {
          console.warn('[composeTabsStore] Failed to clear localStorage after quota exceeded:', cleanupError);
        }
        return;
      }
      throw error;
    }
  },
  removeItem: (name: string) => {
    try {
      localStorage.removeItem(name);
    } catch (error) {
      console.warn('[composeTabsStore] localStorage removeItem failed:', error);
    }
  },
};

export interface ComposeTab {
  id: string;
  subject: string;
  preheader: string;
  to: Array<{ name?: string; address: string }>;
  cc: Array<{ name?: string; address: string }>;
  bcc: Array<{ name?: string; address: string }>;
  body: string;
  toInputValue: string;
  ccInputValue: string;
  bccInputValue: string;
  showBcc: boolean;
  replyTo?: string;
  createdAt: number;
}

interface ComposeTabsState {
  tabs: ComposeTab[];
  activeTabId: string | null;
  showingCompose: boolean;
  chatOpen: boolean;
  viewingChat: boolean; // Track if currently viewing chat (vs mail)
  docsOpen: boolean;
  viewingDocs: boolean;
  sheetsOpen: boolean;
  viewingSheets: boolean;
  aiOpen: boolean;
  viewingAi: boolean;
  panelOrder: string[]; // tracks order panels were opened: 'chat'|'docs'|'sheets'|'ai'
  
  // Actions
  createTab: (options?: { replyTo?: string; subject?: string; to?: Array<{ name?: string; address: string }>; cc?: Array<{ name?: string; address: string }>; body?: string }) => string;
  getOrCreateTab: (options?: { replyTo?: string; subject?: string; to?: Array<{ name?: string; address: string }>; cc?: Array<{ name?: string; address: string }>; body?: string }) => string;
  closeTab: (tabId: string, options?: { skipDraftSave?: boolean }) => void;
  setActiveTab: (tabId: string) => void;
  setShowingCompose: (showing: boolean) => void;
  updateTab: (tabId: string, updates: Partial<ComposeTab>) => void;
  getActiveTab: () => ComposeTab | undefined;
  hasOpenTabs: () => boolean;
  getTabCount: () => number;
  clearAllTabs: () => void;
  cleanupEmptyTabs: () => void;
  openChat: () => void;
  closeChat: () => void;
  setViewingChat: (viewing: boolean) => void;
  openDocs: () => void;
  closeDocs: () => void;
  setViewingDocs: (viewing: boolean) => void;
  openSheets: () => void;
  closeSheets: () => void;
  setViewingSheets: (viewing: boolean) => void;
  openAi: () => void;
  closeAi: () => void;
  setViewingAi: (viewing: boolean) => void;
}

const generateTabId = () => `compose-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const useComposeTabsStore = create<ComposeTabsState>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeTabId: null,
      showingCompose: false,
      chatOpen: false,
      viewingChat: false,
      docsOpen: false,
      viewingDocs: false,
      sheetsOpen: false,
      viewingSheets: false,
      aiOpen: false,
      viewingAi: false,
      panelOrder: [],

      createTab: (options) => {
        console.log('[composeTabsStore] createTab called');
        const newTab: ComposeTab = {
          id: generateTabId(),
          subject: options?.subject || '',
          preheader: '',
          to: options?.to || [],
          cc: options?.cc || [],
          bcc: [],
          body: options?.body || '',
          toInputValue: '',
          ccInputValue: '',
          bccInputValue: '',
          showBcc: false,
          replyTo: options?.replyTo,
          createdAt: Date.now(),
        };

        set((state) => ({
          tabs: [...state.tabs, newTab],
          activeTabId: newTab.id,
          showingCompose: true,
        }));

        return newTab.id;
      },

      // Zoho-style: reuse an existing empty tab if available, otherwise create new
      getOrCreateTab: (options) => {
        const state = get();
        const hasContent = options?.subject || (options?.to && options.to.length > 0) || options?.body;
        
        // If no specific content requested, look for an existing empty tab to reuse
        if (!hasContent) {
          const emptyTab = state.tabs.find(
            (tab) => !tab.subject && tab.to.length === 0
          );
          if (emptyTab) {
            set({ activeTabId: emptyTab.id, showingCompose: true });
            return emptyTab.id;
          }
        }
        
        // Create a new tab
        return get().createTab(options);
      },

      closeTab: (tabId, options) => {
        // Auto-save draft when there is content (recipients, subject, or body)
        // UNLESS skipDraftSave is set (e.g. after a successful send — the email was
        // already sent, so we must NOT leave a stale draft behind).
        const closingTab = get().tabs.find((t) => t.id === tabId);
        if (closingTab && !options?.skipDraftSave) {
          const bodyText = (closingTab.body || '').replace(/<[^>]*>/g, '').trim();
          const hasContent =
            (closingTab.to && closingTab.to.length > 0) ||
            (closingTab.subject && closingTab.subject.trim().length > 0) ||
            bodyText.length > 0;
          if (hasContent) {
            const toList = (closingTab.to || []).map((r: any) => r.address || r);
            const ccList = closingTab.showBcc ? (closingTab.cc || []).map((r: any) => r.address || r) : [];
            const bccList = closingTab.showBcc ? (closingTab.bcc || []).map((r: any) => r.address || r) : [];
            useMailStore.getState().saveDraft({
              to: toList,
              cc: ccList.length > 0 ? ccList : undefined,
              bcc: bccList.length > 0 ? bccList : undefined,
              subject: closingTab.subject || '',
              body: closingTab.body || '',
              htmlBody: closingTab.body || '',
              textBody: bodyText,
            });
          }
        }

        set((state) => {
          const newTabs = state.tabs.filter((t) => t.id !== tabId);
          let newActiveId = state.activeTabId;

          if (state.activeTabId === tabId) {
            // Find next tab to activate
            const closedIndex = state.tabs.findIndex(t => t.id === tabId);
            if (newTabs.length > 0) {
              const newIndex = Math.max(0, closedIndex - 1);
              newActiveId = newTabs[newIndex]?.id || newTabs[0]?.id || null;
            } else {
              newActiveId = null;
            }
          }

          return {
            tabs: newTabs,
            activeTabId: newActiveId,
            showingCompose: newTabs.length > 0 ? state.showingCompose : false,
          };
        });
      },

      setActiveTab: (tabId) => {
        set({ activeTabId: tabId, showingCompose: true, viewingChat: false, viewingDocs: false, viewingSheets: false, viewingAi: false });
      },

      setShowingCompose: (showing) => {
        set({ showingCompose: showing, viewingChat: showing ? false : get().viewingChat, viewingDocs: showing ? false : get().viewingDocs, viewingSheets: showing ? false : get().viewingSheets, viewingAi: showing ? false : get().viewingAi });
      },

      updateTab: (tabId, updates) => {
        set((state) => ({
          tabs: state.tabs.map((tab) =>
            tab.id === tabId ? { ...tab, ...updates } : tab
          ),
        }));
      },

      getActiveTab: () => {
        const state = get();
        return state.tabs.find((t) => t.id === state.activeTabId);
      },

      hasOpenTabs: () => {
        return get().tabs.length > 0;
      },

      getTabCount: () => {
        return get().tabs.length;
      },

      clearAllTabs: () => {
        set({ tabs: [], activeTabId: null, showingCompose: false });
      },

      // Remove tabs with no subject and no recipients (signature-only body doesn't count as content)
      cleanupEmptyTabs: () => {
        set((state) => {
          const nonEmptyTabs = state.tabs.filter(
            (tab) => tab.subject || tab.to.length > 0
          );
          const newActiveId = nonEmptyTabs.length > 0
            ? (nonEmptyTabs.find(t => t.id === state.activeTabId)?.id || nonEmptyTabs[0].id)
            : null;
          // If there are no tabs left and showingCompose was true, turn it off
          const shouldShowCompose = state.showingCompose && nonEmptyTabs.length > 0;
          console.log('[composeTabsStore] cleanupEmptyTabs - removed:', state.tabs.length - nonEmptyTabs.length, 'kept:', nonEmptyTabs.length, 'showingCompose:', state.showingCompose, '->', shouldShowCompose);
          return { 
            tabs: nonEmptyTabs, 
            activeTabId: newActiveId,
            showingCompose: shouldShowCompose,
          };
        });
      },

      openChat: () => {
        console.log('[composeTabsStore] openChat called');
        set((state) => ({
          chatOpen: true, viewingChat: true, viewingDocs: false, viewingSheets: false, viewingAi: false, showingCompose: false,
          panelOrder: state.panelOrder.includes('chat') ? state.panelOrder : [...state.panelOrder, 'chat'],
        }));
      },

      closeChat: () => {
        console.log('[composeTabsStore] closeChat called');
        set((state) => ({ chatOpen: false, viewingChat: false, panelOrder: state.panelOrder.filter(p => p !== 'chat') }));
      },
      
      setViewingChat: (viewing: boolean) => {
        console.log('[composeTabsStore] setViewingChat called:', viewing);
        if (viewing && !get().chatOpen) {
          set({ chatOpen: true, viewingChat: true, viewingDocs: false, viewingSheets: false, viewingAi: false, showingCompose: false });
        } else {
          set({ viewingChat: viewing, viewingDocs: viewing ? false : get().viewingDocs, viewingSheets: viewing ? false : get().viewingSheets, viewingAi: false, showingCompose: viewing ? false : get().showingCompose });
        }
      },

      openDocs: () => {
        set((state) => ({
          docsOpen: true, viewingDocs: true, viewingChat: false, viewingSheets: false, viewingAi: false, showingCompose: false,
          panelOrder: state.panelOrder.includes('docs') ? state.panelOrder : [...state.panelOrder, 'docs'],
        }));
      },
      closeDocs: () => {
        set((state) => ({ docsOpen: false, viewingDocs: false, panelOrder: state.panelOrder.filter(p => p !== 'docs') }));
      },
      setViewingDocs: (viewing: boolean) => {
        if (viewing && !get().docsOpen) {
          set({ docsOpen: true, viewingDocs: true, viewingChat: false, viewingSheets: false, showingCompose: false });
        } else {
          set({ viewingDocs: viewing, viewingChat: viewing ? false : get().viewingChat, viewingSheets: viewing ? false : get().viewingSheets, showingCompose: viewing ? false : get().showingCompose });
        }
      },

      openSheets: () => {
        set((state) => ({
          sheetsOpen: true, viewingSheets: true, viewingChat: false, viewingDocs: false, viewingAi: false, showingCompose: false,
          panelOrder: state.panelOrder.includes('sheets') ? state.panelOrder : [...state.panelOrder, 'sheets'],
        }));
      },
      closeSheets: () => {
        set((state) => ({ sheetsOpen: false, viewingSheets: false, panelOrder: state.panelOrder.filter(p => p !== 'sheets') }));
      },
      setViewingSheets: (viewing: boolean) => {
        if (viewing && !get().sheetsOpen) {
          set({ sheetsOpen: true, viewingSheets: true, viewingChat: false, viewingDocs: false, viewingAi: false, showingCompose: false });
        } else {
          set({ viewingSheets: viewing, viewingChat: viewing ? false : get().viewingChat, viewingDocs: viewing ? false : get().viewingDocs, viewingAi: false, showingCompose: viewing ? false : get().showingCompose });
        }
      },

      openAi: () => {
        set((state) => ({
          aiOpen: true, viewingAi: true, viewingChat: false, viewingDocs: false, viewingSheets: false, showingCompose: false,
          panelOrder: state.panelOrder.includes('ai') ? state.panelOrder : [...state.panelOrder, 'ai'],
        }));
      },
      closeAi: () => {
        set((state) => ({ aiOpen: false, viewingAi: false, panelOrder: state.panelOrder.filter(p => p !== 'ai') }));
      },
      setViewingAi: (viewing: boolean) => {
        if (viewing && !get().aiOpen) {
          set({ aiOpen: true, viewingAi: true, viewingChat: false, viewingDocs: false, viewingSheets: false, showingCompose: false });
        } else {
          set({ viewingAi: viewing, viewingChat: viewing ? false : get().viewingChat, viewingDocs: viewing ? false : get().viewingDocs, viewingSheets: viewing ? false : get().viewingSheets, showingCompose: viewing ? false : get().showingCompose });
        }
      },
    }),
    {
      name: 'zmail-compose-tabs',
      version: 2,
      migrate: (persistedState: unknown, _version: number) => {
        if (persistedState && typeof persistedState === 'object') {
          const ps = persistedState as Record<string, unknown>;
          delete ps.viewingChat;
          delete ps.viewingDocs;
          delete ps.viewingSheets;
        }
        return persistedState as ComposeTabsState;
      },
      merge: (persistedState: unknown, currentState: ComposeTabsState) => {
        const merged = { ...currentState, ...((persistedState as Partial<ComposeTabsState>) || {}) } as ComposeTabsState;
        merged.viewingChat = false;
        merged.viewingDocs = false;
        merged.viewingSheets = false;
        merged.viewingAi = false;
        merged.panelOrder = [];
        return merged;
      },
      storage: createJSONStorage(() => safeStateStorage),
      partialize: (state) => ({
        tabs: state.tabs.map((tab) => ({
          id: tab.id,
          subject: tab.subject,
          preheader: tab.preheader || '',
          to: tab.to,
          cc: tab.cc,
          bcc: tab.bcc,
          body: '',
          toInputValue: '',
          ccInputValue: '',
          bccInputValue: '',
          showBcc: tab.showBcc,
          createdAt: tab.createdAt,
        })),
        activeTabId: state.activeTabId,
        showingCompose: state.showingCompose,
        chatOpen: state.chatOpen,
        viewingChat: state.viewingChat,
        docsOpen: state.docsOpen,
        viewingDocs: state.viewingDocs,
        sheetsOpen: state.sheetsOpen,
        viewingSheets: state.viewingSheets,
      }),
      onRehydrateStorage: () => {
        console.log('[composeTabsStore] Starting hydration from localStorage');
        return (state, error) => {
          if (error) {
            console.error('[composeTabsStore] Hydration error:', error);
          } else if (state) {
            console.log('[composeTabsStore] Hydration complete - chatOpen:', state.chatOpen, 'showingCompose:', state.showingCompose, 'tabs:', state.tabs.length);
            console.log('[composeTabsStore] NOT running cleanup - preserving persisted tabs');
            // Don't cleanup persisted tabs - if user saved them, they want them
          }
        };
      },
    }
  )
);

console.log('[composeTabsStore] Store created, checking localStorage...');
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('zmail-compose-tabs');
  console.log('[composeTabsStore] localStorage value:', stored);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      console.log('[composeTabsStore] Parsed localStorage:', parsed);
    } catch (e) {
      console.error('[composeTabsStore] Failed to parse localStorage:', e);
    }
  }
}

// Log initial state on load
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const state = useComposeTabsStore.getState();
    console.log('[composeTabsStore] Initial state check - chatOpen:', state.chatOpen, 'showingCompose:', state.showingCompose);
  }, 10);
}
